import type { Column, GridApi } from "ag-grid-community";
import { useCallback, useRef } from "react";

import type { SmartTableRowData } from "../types";
import { useSheetDataStore } from "../../../../sheet-data-store-context";
import type { SheetDataStore } from "../../../../stores/sheet-data-store";

import {
  useBatchRecalculateCells,
  type BatchRecalculateCellsResponse,
} from "@/queries/smart-tables";
import { PENDING_CELL_STATUSES } from "@/queries/smart-tables/types";
import { isRunnableColumnType } from "@/routes/smart-table/utils/columnTypeGuards";
import { displayErrorToast } from "@/utils/toast";

interface ColumnContext {
  parentColumnId?: string;
  configColumnId?: string;
}

interface UseRunSelectedCellsOptions {
  tableId?: string;
  sheetId?: string;
}

interface UseRunSelectedCellsResult {
  isPending: boolean;
  runCells: (cellIds: string[]) => void;
}

interface GetRunnableSelectedCellIdsOptions {
  api: GridApi<SmartTableRowData>;
  sheetDataStore: SheetDataStore;
}

const getStorageColumnId = (column: Column): string => {
  const context = column.getColDef().context as ColumnContext | undefined;
  const candidates = [context?.configColumnId, context?.parentColumnId, column.getColId()];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) {
      return candidate;
    }
  }

  return column.getColId();
};

const isServerBackedCellId = (cellId: string): boolean => !cellId.startsWith("temp-");

const getSelectedCellIdsFromRanges = ({
  api,
  sheetDataStore,
  includePending,
}: GetRunnableSelectedCellIdsOptions & {
  includePending: boolean;
}): string[] => {
  const cellRanges = api.getCellRanges() ?? [];
  const selectedCellIds = new Set<string>();

  for (const range of cellRanges) {
    const startIndex = range.startRow?.rowIndex;
    const endIndex = range.endRow?.rowIndex;
    if (typeof startIndex !== "number" || typeof endIndex !== "number") {
      continue;
    }

    const rowStart = Math.min(startIndex, endIndex);
    const rowEnd = Math.max(startIndex, endIndex);
    const storageColumnIds = Array.from(new Set(range.columns.map(getStorageColumnId)));

    for (let displayedRowIndex = rowStart; displayedRowIndex <= rowEnd; displayedRowIndex += 1) {
      const rowNode = api.getDisplayedRowAtIndex(displayedRowIndex);
      const rowIndex = rowNode?.data?._rowIndex;
      if (typeof rowIndex !== "number") {
        continue;
      }

      const cellMap = sheetDataStore.rows.get(rowIndex);
      if (!cellMap) {
        continue;
      }

      for (const columnId of storageColumnIds) {
        const column = sheetDataStore.columns.get(columnId);
        if (!column || !isRunnableColumnType(column.type)) {
          continue;
        }

        const cell = cellMap.get(columnId);
        if (!cell || !isServerBackedCellId(cell.id)) {
          continue;
        }

        if (!includePending && PENDING_CELL_STATUSES.has(cell.status)) {
          continue;
        }

        selectedCellIds.add(cell.id);
      }
    }
  }

  return Array.from(selectedCellIds);
};

export const getRunnableSelectedCellIds = (options: GetRunnableSelectedCellIdsOptions): string[] =>
  getSelectedCellIdsFromRanges({ ...options, includePending: false });

export const getSelectedServerBackedCellIds = (
  options: GetRunnableSelectedCellIdsOptions,
): string[] => getSelectedCellIdsFromRanges({ ...options, includePending: true });

export const useRunSelectedCells = ({
  tableId,
  sheetId,
}: UseRunSelectedCellsOptions): UseRunSelectedCellsResult => {
  const sheetDataStore = useSheetDataStore();
  const batchRecalculateCells = useBatchRecalculateCells();
  const batchRecalculateCellsRef = useRef(batchRecalculateCells);
  batchRecalculateCellsRef.current = batchRecalculateCells;

  const runCells = useCallback(
    (cellIds: string[]) => {
      const uniqueCellIds = Array.from(new Set(cellIds));
      if (!tableId || !sheetId || uniqueCellIds.length === 0) {
        return;
      }

      const snapshots = sheetDataStore.markCellsQueued(uniqueCellIds);

      batchRecalculateCellsRef.current.mutate(
        {
          tableId,
          sheetId,
          cell_ids: uniqueCellIds,
        },
        {
          onSuccess: raw => {
            const response = raw as BatchRecalculateCellsResponse;
            sheetDataStore.bindCellSnapshotsToExecution(snapshots, response.target_execution_id);
            if (typeof response.cell_count === "number" && response.cell_count === 0) {
              sheetDataStore.restoreCellSnapshots(snapshots);
            }
          },
          onError: () => {
            sheetDataStore.restoreCellSnapshots(snapshots);
            displayErrorToast("Failed to run selected cells.");
          },
        },
      );
    },
    [sheetDataStore, sheetId, tableId],
  );

  return { isPending: batchRecalculateCells.isPending, runCells };
};
